/* eslint-disable no-console */
import { filterByBlankCues } from '../filterResponsesByBlank';
import { rematchFillInBlankResponse } from './fillInBlankRematching';
import { requestPost } from '../../../../modules/request/index';

interface Response {
  author?: string;
  count?: number;
  feedback?: string;
  first_attempt_count?: number;
  id: string;
  key: string;
  optimal?: boolean | null;
  text: string;
  [key: string]: any;
}

interface Question {
  blankByBlankGrading?: any;
  caseInsensitive?: boolean;
  conceptID?: string;
  prompt: string;
  [key: string]: any;
}

/**
 * Fetch, filter, and rematch responses affected by a specific cue change in blankByBlankGrading
 *
 * @param questionID - The question's unique identifier
 * @param question - The question object with prompt and grading data
 * @param blankKey - Which blank changed (e.g., "blank1", "blank2")
 * @param cue - The cue value that was modified (e.g., "a", "good")
 * @returns Promise with counts of succeeded and failed rematches
 */
export async function rematchFillInBlankResponsesForCue(
  questionID: string,
  question: Question,
  blankKey: string,
  cue: string
): Promise<{ succeeded: number; failed: number }> {
  try {
    // 1. Fetch all responses for this question
    const allResponses = await fetchAllResponses(questionID);

    // 2. Filter by blank/cue using existing utility
    const blankIndex = parseInt(blankKey.replace('blank', '')) - 1;
    const numberOfBlanks = (question.prompt.match(/___/g) || []).length;

    // Build searchValues array with cue at the changed blank position
    // Example: if blank2 changed and we have 3 blanks: ['', 'cue', '']
    const searchValues = Array(numberOfBlanks).fill('');
    searchValues[blankIndex] = cue;

    const responsesWithCue = filterByBlankCues(allResponses, question, searchValues);

    // 3. Filter by grading status: optimal === null OR author !== null
    const responsesToRematch = responsesWithCue.filter(
      response => response.optimal === null || response.author !== null
    );

    if (responsesToRematch.length === 0) {
      return { succeeded: 0, failed: 0 };
    }

    // 4. Rematch each response sequentially
    let succeeded = 0;
    let failed = 0;

    for (const response of responsesToRematch) {
      try {
        await new Promise<void>((resolve, reject) => {
          try {
            rematchFillInBlankResponse(
              response,
              question,
              questionID,
              () => resolve()
            );
            // Add a timeout to handle cases where callback never fires
            setTimeout(() => reject(new Error('Rematch timeout')), 10000);
          } catch (syncError) {
            reject(syncError);
          }
        });
        succeeded += 1;
      } catch (error) {
        console.error(`Failed to rematch response ${response.id}:`, error);
        failed += 1;
      }
    }

    return { succeeded, failed };
  } catch (error) {
    console.error('Error in rematchFillInBlankResponsesForCue:', error);
    throw error;
  }
}

/**
 * Fetch all responses for a question from the CMS
 */
async function fetchAllResponses(questionID: string): Promise<Response[]> {
  return new Promise((resolve, reject) => {
    requestPost(
      `${window.postbuildEnv.QUILL_CMS}/questions/${questionID}/responses/search`,
      {
        search: {
          filters: {
            author: [],
            status: []
          },
          sort: {
            column: 'count',
            direction: 'desc'
          },
          excludeMisspellings: false,
          text: '',
          pageNumber: 1,
          perPage: 1000
        }
      },
      (data) => {
        const responses = data?.results || [];
        const responsesArray = responses.map((resp: any) => ({
          ...resp,
          key: resp.id
        }));
        resolve(responsesArray);
      },
      (error) => {
        console.error('Error fetching responses:', error);
        reject(error);
      }
    );
  });
}
